import { useEffect, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformArray } from '../lib/transformers'
import type { AuditLog } from '../types'
import { format } from 'date-fns'

export default function AuditLogPage() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    loadAuditLog()
  }, [filter])

  const loadAuditLog = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getAuditLog({ action: filter || undefined })
      let rawData = []
      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {logs: [], pagination: {}}}
        rawData = response.data?.data?.logs || response.data?.logs || response.data?.data || response.data?.items || []
      }
      // Transform snake_case to camelCase
      const data = transformArray<AuditLog>(Array.isArray(rawData) ? rawData : [])
      setLogs(data)
    } catch (error) {
      console.error('Failed to load audit log:', error)
      setLogs([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-gray-600 mt-1">Track all admin actions and changes</p>
      </div>

      <div className="card">
        <div className="mb-6">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by action..."
            className="input max-w-md"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No audit logs found</div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{log.adminUsername}</span>
                      <span className="text-gray-400">•</span>
                      <span className="badge badge-info">{log.action}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{log.resource}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Resource ID: {log.resourceId}
                    </p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                          View details
                        </summary>
                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span>IP: {log.ipAddress}</span>
                      <span>•</span>
                      <span>{format(new Date(log.createdAt), 'PPpp')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
